import { FC } from 'react'
import { useAuth } from '@frontegg/react'
import { Box, CircularProgress, IconButton, Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { UserServiceV4 } from 'services'
import { hasRole, Role } from '../../../../../../lib/utils'
import { useLocation, useNavigate } from 'react-router-dom'

/* Utils */
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { closeToast, openToast, ToastVariant } from '../../../../../../store/toasts/toastSlice'
import {
  closeModal,
  modalPropsSelector,
  ModalType,
  openModal,
  setModalProps
} from '../../../../../../store/modals/modalSlice'
import { ownerRole, roleOptions } from '../../invite-user/InviteUser'

/* Components */
import { Actions, Content, Header } from '../userProfile.styles'
import ControlledRadioGroup from '../../../../form-components/ControlledRadioGroup'
import { CloseChipIcon } from '../../../../svg'
import { ThemeButton } from '../../../../buttons'

const ChangeRole: FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const modalProps = useAppSelector(modalPropsSelector)
  const { user: authUser } = useAuth()

  const handleClose = () => {
    dispatch(closeModal())
  }

  const handleSave = async (values: { role: string }) => {
    if (!modalProps.props) return
    if (!modalProps.props.user) return

    if (values.role && values.role === 'owner' && hasRole(Role.Owner, authUser)) {
      dispatch(
        openModal({
          type: ModalType.transferOwnership,
          props: {
            ...modalProps.props,
            newRole: values.role,
            showTransferInput: location.pathname === '/settings/profile'
          }
        })
      )
      return
    }

    try {
      await UserServiceV4.updateUserRole(modalProps.props.user.id, values.role)

      dispatch(
        setModalProps({
          open: false,
          props: {
            ...modalProps.props,
            user: {
              ...modalProps.props.user,
              role: values.role
            }
          }
        })
      )
      dispatch(
        openToast({
          variant: ToastVariant.success,
          props: {
            text: 'Role Updated',
            description: `The role for ${modalProps.props.user.email} has been updated.`
          }
        })
      )
      if (values.role !== 'admin' && values.role !== 'cpi_manager') {
        navigate('/')
      }
    } catch (e) {
      console.error(e)
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed changing role',
            description: (e as any).response?.data?.message ?? 'There was a problem while updating the role for user.'
          }
        })
      )
    } finally {
      setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
    }
  }

  const getOptions = () => {
    if (modalProps && modalProps.props && modalProps.props.user) {
      if (modalProps.props.user.id === authUser?.id) {
        return roleOptions
      }
      if (hasRole(Role.Admin, authUser)) {
        return roleOptions
      }
    }

    return [ownerRole, ...roleOptions]
  }

  if (!modalProps.props || !modalProps.props.user) {
    return <div>No user found.</div>
  }
  return (
    <Formik
      initialValues={{
        role: modalProps.props.user.role === 'Manager' ? 'cpi_manager' : modalProps.props.user.role.toLowerCase()
      }}
      validationSchema={Yup.object({
        role: Yup.string().required('Required')
      })}
      onSubmit={handleSave}
    >
      {({ isSubmitting, isValid }) => (
        <Form autoComplete="off">
          <Box>
            <Header>
              <Typography className="title">Change Role</Typography>
              <IconButton onClick={handleClose}>
                <CloseChipIcon />
              </IconButton>
            </Header>
            <Content userrole="" className="assign-role-content">
              <Box className="roles-wrapper">
                <Typography className="roles-title">
                  Assign Role <span>*</span>
                </Typography>
                <ControlledRadioGroup name="role" options={getOptions()} />
              </Box>
            </Content>
            <Actions>
              <ThemeButton
                themevariant="primary"
                type="submit"
                disabled={isSubmitting || !isValid}
                endIcon={isSubmitting && <CircularProgress size="20px" />}
              >
                Save
              </ThemeButton>
            </Actions>
          </Box>
        </Form>
      )}
    </Formik>
  )
}

export default ChangeRole
